'use client';

import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { useGateValue } from '@statsig/react-bindings';

import BatteryAndTipsFootnote from '@/app/components/BatteryAndTipsFoonote';
import PageLayout from '@/app/components/PageLayout';
import RichText from '@/app/components/RichText';
import { LEGAL_FOOTNOTES_ID } from '@/app/consts/ids';
import useCurrency from '@/app/hooks/useCurrency';
import usePartnerUtm from '@/app/hooks/usePartnerUtm/usePartnerUtm';
import useScrollTracking from '@/app/hooks/useScrollTracking';

import AccessoriesSection from './_components/AccessoriesSection';
import BuyWithConfidenceSection from './_components/BuyWithConfidenceSection';
import NotSureCompareRingsSection from './_components/NotSureCompareRingsSection';
import { StoreHeaderSection } from './_components/StoreHeaderSection/StoreHeaderSection';
import { StoreNaturalCyclesSection } from './_components/StoreNaturalCyclesSection/StoreNaturalCyclesSection';
import StoreRingCarousel from './_components/StoreRingCarousel/StoreRingCarousel';
import { getOr4CeramicDataArray } from './_data/oR4CeramicData';
import { oR4DataArray } from './_data/oR4Data';
import useRingCarouselData from './_hooks/useRingCarouselData';

const Content = () => {
  // Persist UTM query param data to localStorage (if any params exist)
  usePartnerUtm();
  // scroll depth tracking hook for segment analytics
  useScrollTracking();

  const { currencyCode } = useCurrency();
  const { products: or4Products, priceRange: or4PriceRange } =
    useRingCarouselData({ baseData: oR4DataArray });
  const isMidnightExclusive = useGateValue('ceramic_midnight_exclusive');
  const or4CeramicDataArray = getOr4CeramicDataArray(isMidnightExclusive);

  const { products: or4CeramicProducts, priceRange: or4CeramicPriceRange } =
    useRingCarouselData({
      baseData: or4CeramicDataArray,
    });
  const or4ShowPriceText =
    !!or4PriceRange && or4PriceRange.maxPrice > or4PriceRange.minPrice;
  const or4CeramicShowPriceText =
    !!or4CeramicPriceRange &&
    or4CeramicPriceRange.maxPrice > or4CeramicPriceRange.minPrice;

  return (
    <PageLayout
      pageName="oura-store"
      containerProps={{
        className: 'text-sandstone-500 bg-gray-100',
        name: 'oura-store',
        padding: 'none',
      }}
      footerProps={{ wrapperClassName: 'bg-gray-450' }}
      footnotesProps={{
        id: LEGAL_FOOTNOTES_ID,
        className: 'text-gray-400 bg-gray-100',
        footnotes: [
          {
            content: <BatteryAndTipsFootnote />,
            marker: '*',
          },
          {
            content: <RichText messageKey="footnotes_nc_ourashop_2" />,
            marker: '**',
          },
        ],
      }}
    >
      <LayoutGrid>
        <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main' }}>
          <StoreHeaderSection />
        </GridItem>
        <GridItem
          colEnd={{ sm: 'full', lg: 'full' }}
          colStart={{ sm: 1, lg: 'full' }}
          className="pb-30"
        >
          <section data-cy="or4-ceramic-carousel-section">
            <StoreRingCarousel
              tag={{ children: 'new', variant: 'blue' }}
              title="oura_ring_4_ceramic"
              priceInfo={{
                overviewPrice: or4CeramicPriceRange?.minPrice,
                priceText: or4CeramicShowPriceText ? 'store_from_price' : null,
                currencyCode,
              }}
              productComponents={or4CeramicProducts}
            />
          </section>
        </GridItem>
        <GridItem
          colEnd={{ sm: 'full', lg: 'full' }}
          colStart={{ sm: 1, lg: 'full' }}
          className="pb-30"
        >
          <section data-cy="or4-carousel-section">
            <StoreRingCarousel
              title="oura_ring_4"
              priceInfo={{
                overviewPrice: or4PriceRange?.minPrice,
                priceText: or4ShowPriceText ? 'store_from_price' : null,
                currencyCode,
              }}
              productComponents={or4Products}
            />
          </section>
        </GridItem>
        <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main' }}>
          <NotSureCompareRingsSection />
        </GridItem>
        <GridItem
          colEnd={{ sm: 'full' }}
          colStart={{ sm: 'full' }}
          className="h-full"
        >
          <BuyWithConfidenceSection />
        </GridItem>
        <GridItem colEnd={{ sm: 'full' }} colStart={{ sm: 'full' }}>
          <AccessoriesSection />
        </GridItem>
        <GridItem colEnd={{ sm: 'main' }} colStart={{ sm: 'main' }}>
          <StoreNaturalCyclesSection />
        </GridItem>
      </LayoutGrid>
    </PageLayout>
  );
};

export default Content;
